// components/domain-assignment/AssignmentDetailView.js
import React from 'react';
import {ArrowLeft, BookOpenCheck, Edit, Eye, Trash2} from 'lucide-react';
import ActivityMarkdown from './components/ActivityMarkdown';
import RubricTable from './components/RubricTable';

const AssignmentDetailView = ({
                                  assignment,
                                  isPreparatoryStage = false, // Prop to determine Preparatory Stage UI
                                  isMiddleStage = false, // Prop to determine Middle Stage UI
                                  onBack,
                                  onEdit,
                                  onViewSubmissions,
                                  onDelete
                              }) => {

    // Helper function to determine activity approach checkmarks for Middle Stage
    const getActivityApproaches = () => {
        if (!assignment || !assignment.activity_approach) return {
            artIntegrated: false,
            sportsIntegrated: false,
            toyBased: false,
            technologyIntegrated: false,
            anyOther: false,
            otherDescription: ''
        };

        // Handle both API formats
        if (typeof assignment.activity_approach === 'object') {
            // Handle nested object format from API
            if (assignment.activity_approach.art_integrated !== undefined) {
                return {
                    artIntegrated: assignment.activity_approach.art_integrated || false,
                    sportsIntegrated: assignment.activity_approach.sports_integrated || false,
                    toyBased: assignment.activity_approach.toy_based || false,
                    technologyIntegrated: assignment.activity_approach.technology_integrated || false,
                    anyOther: assignment.activity_approach.any_other || false,
                    otherDescription: assignment.activity_approach.other_description || ''
                };
            }
            // Handle front-end format
            return {
                artIntegrated: assignment.activity_approach.art || false,
                sportsIntegrated: assignment.activity_approach.sports || false,
                toyBased: assignment.activity_approach.toy || false,
                technologyIntegrated: assignment.activity_approach.tech || false,
                anyOther: assignment.activity_approach.other || false,
                otherDescription: assignment.activity_approach.other_text || assignment.activity_approach.otherText || ''
            };
        }

        // Handle array format
        if (Array.isArray(assignment.activity_approach)) {
            return {
                artIntegrated: assignment.activity_approach.includes("Art-integrated"),
                sportsIntegrated: assignment.activity_approach.includes("Sports-integrated"),
                toyBased: assignment.activity_approach.includes("Toy-based"),
                technologyIntegrated: assignment.activity_approach.includes("Technology-integrated"),
                anyOther: assignment.activity_approach.some(a =>
                    !["Art-integrated", "Sports-integrated", "Toy-based", "Technology-integrated"].includes(a)),
                otherDescription: assignment.activity_approach.find(a =>
                    !["Art-integrated", "Sports-integrated", "Toy-based", "Technology-integrated"].includes(a)) || ''
            };
        }

        return {
            artIntegrated: false,
            sportsIntegrated: false,
            toyBased: false,
            technologyIntegrated: false,
            anyOther: false,
            otherDescription: ''
        };
    };

    // Helper function to process the assessment rubric for display
    const processRubric = () => {
        if (!assignment || !assignment.assessment_rubric) return [];

        // For debugging
        console.log("Original rubric:", assignment.assessment_rubric);

        // Check if the rubric is already in the expected format
        if (assignment.assessment_rubric.length > 0) {
            return assignment.assessment_rubric.map(item => {
                // Extract the criterion/ability name
                let criterion = item.ability || item.criterion || '';

                // Keep the full ability name for display in the middle stage
                // but still use the appropriate styling class

                // Handle different rubric formats
                if (item.beginner !== undefined || item.proficient !== undefined || item.advanced !== undefined) {
                    return {
                        criterion: criterion,
                        beginner: item.beginner || '',
                        proficient: item.proficient || '',
                        advanced: item.advanced || ''
                    };
                }

                if (item.level !== undefined) {
                    // This is the API format with ability and level
                    return {
                        criterion: criterion,
                        level: item.level,
                        beginner: item.level === "Beginner" ? "✓" : '',
                        proficient: item.level === "Proficient" ? "✓" : '',
                        advanced: item.level === "Advanced" ? "✓" : ''
                    };
                }

                // Legacy format with stream/mountain/sky
                return {
                    criterion: criterion,
                    beginner: item.stream || '',
                    proficient: item.mountain || '',
                    advanced: item.sky || ''
                };
            });
        }

        return [];
    };

    // Helper function to get assessment questions
    const getAssessmentQuestions = () => {
        if (assignment.assessment_questions && Array.isArray(assignment.assessment_questions)) {
            return assignment.assessment_questions;
        }
        if (assignment.assessment_question) {
            return [assignment.assessment_question];
        }
        return [];
    };

    // Render the Middle Stage UI (new design based on the form)
    const renderMiddleStageUI = () => {
        const activityApproaches = getActivityApproaches();
        const processedRubric = processRubric();
        const assessmentQuestions = getAssessmentQuestions();

        return (
            <div className="bg-white shadow-lg rounded-lg overflow-hidden border border-orange-200">
                {/* PART B Header */}
                <div className="text-center bg-orange-500 text-white py-3 font-bold text-xl">
                    PART B
                </div>

                {/* Learning Standard Header */}
                <div className="text-center bg-orange-500 text-white py-2 border-t border-orange-400 font-bold">
                    {assignment.domain || "Learning Standard"}
                </div>

                {/* Curricular Goals */}
                <div className="border-b border-orange-300">
                    <div className="bg-orange-100 p-3 border-b border-orange-300">
                        <div className="flex justify-between items-center">
                            <h3 className="font-bold text-gray-800">Curricular Goals</h3>
                            <span className="text-sm text-gray-600 italic">(Choose one or more)</span>
                        </div>
                    </div>

                    <div className="p-4">
                        {assignment.curricular_goals && assignment.curricular_goals.length > 0 ? (
                            <div className="grid grid-cols-5 gap-4">
                                {assignment.curricular_goals.map((goalId) => (
                                    <div key={goalId} className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id={`goal-${goalId}`}
                                            checked={true}
                                            readOnly
                                            className="h-4 w-4 text-orange-600 focus:ring-orange-500 mr-2"
                                        />
                                        <label htmlFor={`goal-${goalId}`} className="text-sm font-medium text-gray-700">
                                            {goalId}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-gray-500 italic">No curricular goals specified</div>
                        )}
                    </div>
                </div>

                {/* Competencies */}
                <div className="border-b border-orange-300">
                    <div className="bg-orange-100 p-3 border-b border-orange-300">
                        <div className="flex justify-between items-center">
                            <h3 className="font-bold text-gray-800">Competencies</h3>
                            <span className="text-sm text-gray-600 italic">(Choose one or more)</span>
                        </div>
                    </div>

                    <div className="p-4">
                        {assignment.competencies && assignment.competencies.length > 0 ? (
                            <div className="grid grid-cols-5 gap-4">
                                {assignment.competencies.map((compId) => (
                                    <div key={compId} className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id={`comp-${compId}`}
                                            checked={true}
                                            readOnly
                                            className="h-4 w-4 text-orange-600 focus:ring-orange-500 mr-2"
                                        />
                                        <label htmlFor={`comp-${compId}`} className="text-sm font-medium text-gray-700">
                                            {compId}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-gray-500 italic">No competencies specified</div>
                        )}
                    </div>
                </div>

                {/* ACTIVITY Header */}
                <div className="text-center bg-orange-500 text-white py-2 font-bold text-xl">
                    ACTIVITY
                </div>

                {/* Activity Approach */}
                <div className="border-b border-orange-300">
                    <div className="bg-orange-100 p-3 border-b border-orange-300">
                        <div className="flex items-center">
                            <h3 className="font-bold text-gray-800">Approach of the Activity:</h3>
                            <span className="text-sm text-gray-600 italic ml-2">(Please ✓ all that apply)</span>
                        </div>
                    </div>

                    <div className="p-4 grid grid-cols-2 gap-4">
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="art-integrated"
                                checked={activityApproaches.artIntegrated}
                                readOnly
                                className="h-4 w-4 text-orange-600 focus:ring-orange-500 mr-2"
                            />
                            <label htmlFor="art-integrated" className="text-sm font-medium text-gray-700">
                                Art-integrated
                            </label>
                        </div>
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="sports-integrated"
                                checked={activityApproaches.sportsIntegrated}
                                readOnly
                                className="h-4 w-4 text-orange-600 focus:ring-orange-500 mr-2"
                            />
                            <label htmlFor="sports-integrated" className="text-sm font-medium text-gray-700">
                                Sports-integrated
                            </label>
                        </div>
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="toy-based"
                                checked={activityApproaches.toyBased}
                                readOnly
                                className="h-4 w-4 text-orange-600 focus:ring-orange-500 mr-2"
                            />
                            <label htmlFor="toy-based" className="text-sm font-medium text-gray-700">
                                Toy-based
                            </label>
                        </div>
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="technology-integrated"
                                checked={activityApproaches.technologyIntegrated}
                                readOnly
                                className="h-4 w-4 text-orange-600 focus:ring-orange-500 mr-2"
                            />
                            <label htmlFor="technology-integrated" className="text-sm font-medium text-gray-700">
                                Technology-integrated
                            </label>
                        </div>
                        <div className="flex items-center col-span-2">
                            <input
                                type="checkbox"
                                id="other-approach"
                                checked={activityApproaches.anyOther}
                                readOnly
                                className="h-4 w-4 text-orange-600 focus:ring-orange-500 mr-2"
                            />
                            <label htmlFor="other-approach" className="text-sm font-medium text-gray-700 mr-2">
                                Any Other
                            </label>
                            {activityApproaches.anyOther && activityApproaches.otherDescription && (
                                <span className="text-sm text-gray-700 ml-2 px-2 py-1 bg-gray-100 rounded">
                                    {activityApproaches.otherDescription}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Activity */}
                <div className="border-b border-orange-300">
                    <div className="bg-orange-100 p-3 border-b border-orange-300">
                        <h3 className="font-bold text-gray-800">Activity:</h3>
                    </div>
                    <div className="p-4">
                        <div className="p-4 bg-white border border-orange-200 rounded-lg min-h-32">
                            <ActivityMarkdown content={assignment.activity || "No activity description provided"}/>
                        </div>
                    </div>
                </div>

                {/* Assessment Questions - Display all questions with numbers */}
                <div className="border-b border-orange-300">
                    <div className="bg-orange-100 p-3 border-b border-orange-300">
                        <h3 className="font-bold text-gray-800">Assessment Questions:</h3>
                    </div>
                    <div className="p-4">
                        <div className="p-4 bg-white border border-orange-200 rounded-lg">
                            {assessmentQuestions.length > 0 ? (
                                <div className="space-y-4">
                                    {assessmentQuestions.map((question, index) => (
                                        <div key={index} className="mb-2">
                                            <span className="font-medium text-gray-700">Question {index + 1}: </span>
                                            <span className="text-gray-800">{question}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <span className="text-gray-500 italic">No assessment questions provided</span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Assessment Rubric */}
                <div>
                    <div className="text-center bg-orange-500 text-white py-2 font-bold text-xl">
                        ASSESSMENT RUBRIC
                    </div>
                    <div className="border-t-0 rounded-b-lg overflow-hidden">
                        {processedRubric.length > 0 ? (
                            <>
                                <table className="min-w-full border-collapse">
                                    <thead>
                                    <tr>
                                        <th className="border border-orange-300 bg-orange-100 p-3 text-left text-orange-800 font-semibold w-1/4">
                                            Abilities
                                        </th>
                                        <th className="border border-orange-300 bg-orange-100 p-3 text-center text-orange-800 font-semibold w-1/4">
                                            Beginner
                                        </th>
                                        <th className="border border-orange-300 bg-orange-100 p-3 text-center text-orange-800 font-semibold w-1/4">
                                            Proficient
                                        </th>
                                        <th className="border border-orange-300 bg-orange-100 p-3 text-center text-orange-800 font-semibold w-1/4">
                                            Advanced
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {processedRubric.map((row, index) => (
                                        <tr key={index}>
                                            <td className={`border border-orange-200 p-3 font-medium ${
                                                row.criterion?.includes("Awareness")
                                                    ? "bg-pink-200"
                                                    : row.criterion?.includes("Sensitivity")
                                                        ? "bg-yellow-100"
                                                        : "bg-blue-100"
                                            }`}>
                                                {row.criterion || 'Ability'}
                                            </td>
                                            <td className="border border-orange-200 p-3 text-sm whitespace-pre-wrap">
                                                {row.beginner || row.stream || (row.level === "Beginner" ? "✓" : "")}
                                            </td>
                                            <td className="border border-orange-200 p-3 text-sm whitespace-pre-wrap">
                                                {row.proficient || row.mountain || (row.level === "Proficient" ? "✓" : "")}
                                            </td>
                                            <td className="border border-orange-200 p-3 text-sm whitespace-pre-wrap">
                                                {row.advanced || row.sky || (row.level === "Advanced" ? "✓" : "")}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                                <div className="p-3 text-sm text-gray-600 flex items-start">
                                    <span className="font-bold mr-1">* Note:</span>
                                    <span>Circle the relevant performance level based on the individual student's performance for each ability for this activity.</span>
                                </div>
                            </>
                        ) : (
                            <div className="p-4 text-center text-gray-500 italic">
                                No assessment rubric provided
                            </div>
                        )}
                    </div>
                </div>

                {/* Assignment Information */}
                <div className="p-4 bg-gray-50 border-t border-orange-200">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">Assignment Information:</h3>
                    <div className="p-4 bg-white rounded-lg border border-gray-200">
                        <p className="text-gray-700">
                            <span className="font-medium">Assignment Type:</span>{' '}
                            {assignment.assign_to_all ? 'Assigned to all students' : 'Assigned to specific students'}
                        </p>
                        {!assignment.assign_to_all && assignment.student_ids && (
                            <div className="mt-2">
                                <p className="font-medium text-gray-700">Assigned
                                    to {assignment.student_ids.length} students:</p>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {assignment.student_ids.map((studentId, index) => (
                                        <span
                                            key={index}
                                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                                        >
                                            Student ID: {studentId.substring(0, 8)}...
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                        <p className="mt-2 text-gray-700">
                            <span className="font-medium">Created on:</span>{' '}
                            {assignment.created_at ? new Date(assignment.created_at).toLocaleDateString(undefined, {
                                year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
                            }) : 'Date not available'}
                        </p>
                    </div>
                </div>
            </div>
        );
    };

    // Render the Preparatory Stage UI (new design)
    const renderPreparatoryUI = () => {
        const processedRubric = processRubric();
        const assessmentQuestions = getAssessmentQuestions();

        return (
            <div className="bg-white shadow-lg rounded-lg overflow-hidden border border-orange-200">
                {/* PART B Header */}
                <div className="text-center bg-orange-500 text-white py-3 font-bold text-xl">
                    PART B
                </div>

                {/* Learning Standard Header */}
                <div className="text-center bg-orange-500 text-white py-2 border-t border-orange-400 font-bold">
                    Learning Standard: {assignment.domain}
                </div>

                {/* Curricular Goals */}
                <div className="border-b border-orange-300">
                    <div className="bg-orange-100 p-3 border-b border-orange-300">
                        <div className="flex justify-between items-center">
                            <h3 className="font-bold text-gray-800">Curricular Goals</h3>
                            <span className="text-sm text-gray-600 italic">(Can choose one or more)</span>
                        </div>
                    </div>

                    <div className="p-4">
                        {assignment.curricular_goals && assignment.curricular_goals.length > 0 ? (
                            <div className="grid grid-cols-5 gap-4">
                                {assignment.curricular_goals.map((goalId) => (
                                    <div key={goalId} className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id={`goal-${goalId}`}
                                            checked={true}
                                            readOnly
                                            className="h-4 w-4 text-orange-600 focus:ring-orange-500 mr-2"
                                        />
                                        <label htmlFor={`goal-${goalId}`} className="text-sm font-medium text-gray-700">
                                            {goalId}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-gray-500 italic">No curricular goals specified</div>
                        )}
                    </div>
                </div>

                {/* Competencies */}
                <div className="border-b border-orange-300">
                    <div className="bg-orange-100 p-3 border-b border-orange-300">
                        <div className="flex justify-between items-center">
                            <h3 className="font-bold text-gray-800">Competencies</h3>
                            <span className="text-sm text-gray-600 italic">(Can choose one or more)</span>
                        </div>
                    </div>

                    <div className="p-4">
                        {assignment.competencies && assignment.competencies.length > 0 ? (
                            <div className="grid grid-cols-5 gap-4">
                                {assignment.competencies.map((compId) => (
                                    <div key={compId} className="flex items-center">
                                        <input
                                            type="checkbox"
                                            id={`comp-${compId}`}
                                            checked={true}
                                            readOnly
                                            className="h-4 w-4 text-orange-600 focus:ring-orange-500 mr-2"
                                        />
                                        <label htmlFor={`comp-${compId}`} className="text-sm font-medium text-gray-700">
                                            {compId}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-gray-500 italic">No competencies specified</div>
                        )}
                    </div>
                </div>

                {/* Activity */}
                <div className="border-b border-orange-300">
                    <div className="bg-orange-100 p-3 border-b border-orange-300">
                        <h3 className="font-bold text-gray-800">Activity</h3>
                    </div>
                    <div className="p-4 bg-orange-50">
                        <div className="p-4 bg-white border border-orange-200 rounded-lg">
                            <ActivityMarkdown content={assignment.activity || "No activity description provided"}/>
                        </div>
                    </div>
                </div>

                {/* Assessment Questions */}
                <div className="border-b border-orange-300">
                    <div className="bg-orange-100 p-3 border-b border-orange-300">
                        <h3 className="font-bold text-gray-800">Assessment Questions</h3>
                    </div>
                    <div className="p-4 bg-orange-50">
                        {assessmentQuestions.length > 0 ? (
                            <ol className="list-decimal ml-6 space-y-2">
                                {assessmentQuestions.map((question, index) => (
                                    <li key={index} className="text-gray-700">{question}</li>
                                ))}
                            </ol>
                        ) : (
                            <div className="text-gray-500 italic">No assessment questions provided</div>
                        )}
                    </div>
                </div>

                {/* Assessment Rubric */}
                <div>
                    {/* Rubric Header */}
                    <div className="text-center bg-orange-500 text-white py-2 font-bold text-xl">
                        ASSESSMENT RUBRIC*
                    </div>

                    {/* Rubric Table */}
                    <div className="border-t-0 rounded-b-lg overflow-hidden">
                        {processedRubric.length > 0 ? (
                            <>
                                <table className="min-w-full border-collapse">
                                    <thead>
                                    <tr>
                                        <th className="border border-orange-300 bg-orange-100 p-3 text-left text-orange-800 font-semibold w-1/4">
                                            Abilities
                                        </th>
                                        <th className="border border-orange-300 bg-orange-100 p-3 text-center text-orange-800 font-semibold w-1/4">
                                            Beginner
                                        </th>
                                        <th className="border border-orange-300 bg-orange-100 p-3 text-center text-orange-800 font-semibold w-1/4">
                                            Proficient
                                        </th>
                                        <th className="border border-orange-300 bg-orange-100 p-3 text-center text-orange-800 font-semibold w-1/4">
                                            Advanced
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {processedRubric.map((row, index) => (
                                        <tr key={index}>
                                            <td className="border border-orange-200 p-3 font-medium">{row.criterion || 'Ability'}</td>
                                            <td className="border border-orange-200 p-3 text-sm">
                                                {row.beginner || row.stream || ''}
                                            </td>
                                            <td className="border border-orange-200 p-3 text-sm">
                                                {row.proficient || row.mountain || ''}
                                            </td>
                                            <td className="border border-orange-200 p-3 text-sm">
                                                {row.advanced || row.sky || ''}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                                <div className="p-3 text-sm text-gray-600 flex items-start">
                                    <span className="font-bold mr-1">* Note:</span>
                                    <span>Assessment rubric for the performance levels of each ability.</span>
                                </div>
                            </>
                        ) : (
                            <div className="p-4 text-center text-gray-500 italic">
                                No assessment rubric provided
                            </div>
                        )}
                    </div>
                </div>

                {/* Assignment Information */}
                <div className="p-4 bg-gray-50 border-t border-orange-200">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">Assignment Information:</h3>
                    <div className="p-4 bg-white rounded-lg border border-gray-200">
                        <p className="text-gray-700">
                            <span className="font-medium">Assignment Type:</span>{' '}
                            {assignment.assign_to_all ? 'Assigned to all students' : 'Assigned to specific students'}
                        </p>
                        {!assignment.assign_to_all && assignment.student_ids && (
                            <div className="mt-2">
                                <p className="font-medium text-gray-700">Assigned
                                    to {assignment.student_ids.length} students:</p>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {assignment.student_ids.map((studentId, index) => (
                                        <span
                                            key={index}
                                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                                        >
                                            Student ID: {studentId.substring(0, 8)}...
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                        <p className="mt-2 text-gray-700">
                            <span className="font-medium">Created on:</span>{' '}
                            {assignment.created_at ? new Date(assignment.created_at).toLocaleDateString(undefined, {
                                year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
                            }) : 'Date not available'}
                        </p>
                    </div>
                </div>
            </div>
        );
    };

    // Render the Foundational Stage UI (original design)
    const renderFoundationalUI = () => {
        const assessmentQuestions = getAssessmentQuestions();

        return (
            <div
                className="group bg-white/80 backdrop-blur-sm overflow-hidden shadow-xl rounded-3xl hover:shadow-2xl border border-white/50 hover:bg-white/90">
                {/* Domain Header */}
                <div className="bg-[#D67C2C] text-white p-4 flex items-center justify-center">
                    <BookOpenCheck className="mr-2" size={24}/>
                    <h2 className="text-xl font-semibold">
                        DOMAIN: {assignment.domain}
                    </h2>
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Created Date */}
                    <div className="mb-6 text-sm text-gray-500">
                        Created
                        on: {assignment.created_at ? new Date(assignment.created_at).toLocaleDateString(undefined, {
                        year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
                    }) : 'Date not available'}
                    </div>

                    {/* Curricular Goals */}
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">Curricular Goals:</h3>
                        {assignment.curricular_goals && assignment.curricular_goals.length > 0 ? (
                            <ul className="space-y-2 ml-4">
                                {assignment.curricular_goals.map(goalId => (
                                    <li key={goalId} className="flex items-start">
                                        <div
                                            className="flex-shrink-0 mr-2 mt-1 w-4 h-4 rounded-full bg-orange-500 text-white flex items-center justify-center text-xs">✓
                                        </div>
                                        <span className="text-gray-700">{goalId}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className="text-gray-500 italic">No curricular goals specified</div>
                        )}
                    </div>

                    {/* Competencies */}
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">Competencies:</h3>
                        {assignment.competencies && assignment.competencies.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                                {assignment.competencies.map(competencyId => (
                                    <span
                                        key={competencyId}
                                        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800"
                                    >
                                        {competencyId}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <div className="text-gray-500 italic">No competencies specified</div>
                        )}
                    </div>

                    {/* Activity */}
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">Activity:</h3>
                        <div className="p-4 bg-gray-50 rounded-lg">
                            <ActivityMarkdown content={assignment.activity || "No activity description provided"}/>
                        </div>
                    </div>

                    {/* Assessment Questions */}
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">Assessment Questions:</h3>
                        {assessmentQuestions.length > 0 ? (
                            <ol className="list-decimal ml-6 space-y-2">
                                {assessmentQuestions.map((question, index) => (
                                    <li key={index} className="text-gray-700">{question}</li>
                                ))}
                            </ol>
                        ) : (
                            <div className="text-gray-500 italic">No assessment questions provided</div>
                        )}
                    </div>

                    {/* Assessment Rubric */}
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">Assessment Rubric:</h3>
                        <div className="overflow-x-auto">
                            {processRubric().length > 0 ? (
                                <table className="min-w-full border-collapse border border-gray-300">
                                    <thead>
                                        <tr>
                                            <th className="border border-gray-300 bg-orange-100 p-3 text-left text-orange-800 font-semibold w-1/4">
                                                Abilities
                                            </th>
                                            <th className="border border-gray-300 bg-blue-100 p-3 text-center text-blue-800 font-semibold w-1/4">
                                                Stream
                                            </th>
                                            <th className="border border-gray-300 bg-green-100 p-3 text-center text-green-800 font-semibold w-1/4">
                                                Mountain
                                            </th>
                                            <th className="border border-gray-300 bg-purple-100 p-3 text-center text-purple-800 font-semibold w-1/4">
                                                Sky
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {processRubric().map((row, index) => (
                                            <tr key={index}>
                                                <td className="border border-gray-300 p-3 font-medium bg-orange-50">
                                                    {row.criterion || 'Ability'}
                                                </td>
                                                <td className="border border-gray-300 p-3 text-sm bg-blue-50">
                                                    {row.beginner || row.stream || ''}
                                                </td>
                                                <td className="border border-gray-300 p-3 text-sm bg-green-50">
                                                    {row.proficient || row.mountain || ''}
                                                </td>
                                                <td className="border border-gray-300 p-3 text-sm bg-purple-50">
                                                    {row.advanced || row.sky || ''}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div className="p-4 text-center text-gray-500 italic border border-gray-300 rounded">
                                    No assessment rubric provided
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Assignment Information */}
                    <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">Assignment Information:</h3>
                        <div className="p-4 bg-gray-50 rounded-lg">
                            <p className="text-gray-700">
                                <span className="font-medium">Assignment Type:</span>{' '}
                                {assignment.assign_to_all ? 'Assigned to all students' : 'Assigned to specific students'}
                            </p>
                            {!assignment.assign_to_all && assignment.student_ids && (
                                <div className="mt-2">
                                    <p className="font-medium text-gray-700">Assigned
                                        to {assignment.student_ids.length} students:</p>
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {assignment.student_ids.map((studentId, index) => (
                                            <span
                                                key={index}
                                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                                            >
                                                Student ID: {studentId.substring(0, 8)}...
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6 font-[poppins]">
            {/* Header with navigation */}
            <div className="mb-6 font-[poppins]">
                {onBack && (
                    <button
                        onClick={onBack}
                        className="inline-flex items-center text-sm font-medium text-orange-500 hover:text-orange-900"
                    >
                        <ArrowLeft size={16} className="mr-1"/>
                        Back to Assignments
                    </button>
                )}

                <div className="mt-3 flex flex-wrap items-center justify-between">
                    <h1 className="text-2xl font-bold text-gray-900">Assignment Details</h1>
                    {(onEdit || onViewSubmissions || onDelete) && (
                        <div className="mt-3 sm:mt-0 flex space-x-2">
                            {onEdit && (
                                <button
                                    onClick={onEdit}
                                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                                >
                                    <Edit size={16} className="mr-1.5"/>
                                    Edit
                                </button>
                            )}
                            {onViewSubmissions && (
                                <button
                                    onClick={onViewSubmissions}
                                    className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[#D67C2C] hover:bg-orange-700 focus:outline-none"
                                >
                                    <Eye size={16} className="mr-1.5"/>
                                    View Submissions
                                </button>
                            )}
                            {onDelete && (
                                <button
                                    onClick={onDelete}
                                    className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none"
                                >
                                    <Trash2 size={16} className="mr-1.5"/>
                                    Delete
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Render different UI based on the stage */}
            {isMiddleStage ? renderMiddleStageUI() :
                isPreparatoryStage ? renderPreparatoryUI() :
                    renderFoundationalUI()}
        </div>
    );
};

export default AssignmentDetailView;